/**
 * Shown when the local IndexedDB could not be opened OR auto-recovered
 * (`LocalDbOpenError` from `initLocalDB`). The hardened open path already tried
 * a bounded retry and a throttled delete-and-recreate; reaching here means the
 * user is otherwise stuck at boot. Logging them out does NOT help — `clearLocalDB`
 * needs an open DB — so we offer the two things that do: reload (re-runs the
 * retry) and an explicit, throttle-bypassing "reset local data" escape hatch.
 *
 * Messages live on the server, so a reset is non-destructive to real data — it
 * re-hydrates via sync on the next open. Tailwind classes mirror
 * UnsupportedBrowserScreen / ErrorFallback for visual consistency.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@primitives/button";
import { resetLocalDatabase } from "@/indexedDB/db";

export default function DbRecoveryScreen() {
  const { t } = useTranslation();
  const [resetting, setResetting] = useState(false);

  const reload = () => window.location.reload();

  const handleReset = async () => {
    setResetting(true);
    try {
      await resetLocalDatabase();
    } catch {
      // Swallow — we reload regardless (a fresh open recreates the schema), and
      // an uncaught rejection here would surface as spurious global-handler noise.
    } finally {
      reload();
    }
  };

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center p-6"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-input bg-background p-6 shadow-sm">
        <h1 className="text-lg font-semibold">
          {t("dbRecovery.title", "Couldn’t open local storage")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "dbRecovery.body",
            "Your device blocked or corrupted this app’s local database. Trying again usually fixes it.",
          )}
        </p>
        <p className="text-sm text-muted-foreground">
          {t(
            "dbRecovery.resetHint",
            "If it keeps happening, reset the local data — your messages are stored securely on the server and will sync back.",
          )}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="default" onClick={reload} disabled={resetting}>
            {t("dbRecovery.retry", "Try again")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={resetting}
          >
            {resetting
              ? t("dbRecovery.resetting", "Resetting…")
              : t("dbRecovery.reset", "Reset local data")}
          </Button>
        </div>
      </div>
    </div>
  );
}
